// Static photo spots & camera tips — 100% offline
// Matched against day title + description via keyword detection
import { detectCountry } from "./emergencyData";

// ── Data structure per destination ──────────────────────────────────────────
// keywords: strings to match in the day title/description (case-insensitive)
// Each entry = one photo spot card
const PHOTO_SPOTS = {

  // ── NAMIBIE ──────────────────────────────────────────────────────────────
  NAMIBIA: [
    {
      keywords: ["sossusvlei", "dead vlei", "deadvlei", "dune", "namib", "sossus"],
      locationName: "Dunes de Sossusvlei — Dead Vlei",
      bestTime: "Lever du soleil (5h30–7h30)",
      phoneSettings: "Mode Pro · ISO 100 · f/8 · 1/200s\nGrand angle (16–24mm) · Horizon au tiers supérieur · HDR off",
      diegosTip: "Arrivez 45 min avant le lever du soleil — la lumière rasante sur les dunes orangées est unique et la foule encore absente.",
    },
    {
      keywords: ["etosha", "trou d'eau", "waterhole", "pan", "faune", "safari", "animaux"],
      locationName: "Points d'eau d'Etosha",
      bestTime: "Heure dorée soir (17h–19h) · Nuit (21h–23h)",
      phoneSettings: "Jour · ISO 400 · f/5.6 · 1/500s (geler le mouvement)\nNuit · ISO 3200 · 20–25s · trépied obligatoire",
      diegosTip: "Choisissez un seul point d'eau et attendez — la patience d'une heure vaut dix heures de roulage dans le parc.",
    },
    {
      keywords: ["spitzkoppe", "spitzkop", "granite", "rocher", "roche", "damaraland"],
      locationName: "Spitzkoppe — Inselbergs de granit rose",
      bestTime: "Coucher de soleil · Voie Lactée (nouvelle lune, 21h–3h)",
      phoneSettings: "Voie Lactée · ISO 6400 · f/2.8 · 20s · trépied\nJour · ISO 100 · f/11 · 1/125s · polarisant",
      diegosTip: "Pointez vers le sud en juin–août — la Voie Lactée est spectaculaire et la pollution lumineuse pratiquement nulle à 300 km de toute ville.",
    },
    {
      keywords: ["fish river", "canyon", "gorge", "ai-ais"],
      locationName: "Fish River Canyon — Deuxième plus grand canyon du monde",
      bestTime: "Lever du soleil (point de vue Hobas)",
      phoneSettings: "Mode Panorama · ISO 100 · f/8 · Règle des tiers\nPolarisant pour atténuer la brume de chaleur",
      diegosTip: "Installez-vous au bord du canyon 30 min avant le lever — les couleurs passent de violet-noir à or en seulement 8 minutes.",
    },
    {
      keywords: ["swakopmund", "walvis", "skeleton", "côte", "atlantique", "brume", "mer"],
      locationName: "Swakopmund — Rencontre désert & Atlantique",
      bestTime: "Matin brumeux (7h–9h) pour les silhouettes",
      phoneSettings: "Brume · ISO 400 · f/5.6 · 1/250s · Monochrome\nLong exposure plage · ND1000 · 30–60s pour lisser les vagues",
      diegosTip: "La brume matinale donne des clichés quasi-fantomatiques — les dunes et la mer fusionnent dans un brouillard soyeux qu'on ne voit nulle part ailleurs.",
    },
    {
      keywords: ["windhoek", "capitale", "katutura", "old location"],
      locationName: "Windhoek — Contrastes coloniaux & townships",
      bestTime: "Heure bleue (6h–7h) · Coucher de soleil depuis Christuskirche",
      phoneSettings: "Architecture · f/8 · ISO 100 · 1/160s · Focale standard\nStreet photo townships · f/2.8 · ISO 800 · 1/320s",
      diegosTip: "Demandez toujours la permission avant de photographier dans Katutura — les habitants sont généreux, mais le respect prime sur tout.",
    },
  ],

  // ── MAURICE ──────────────────────────────────────────────────────────────
  MAURITIUS: [
    {
      keywords: ["chamarel", "sept couleurs", "7 couleurs", "terre colorée", "terre aux"],
      locationName: "Terres des 7 couleurs de Chamarel",
      bestTime: "Matin (8h–10h) avant la chaleur · ciel clair obligatoire",
      phoneSettings: "f/8 · ISO 100 · 1/200s · Saturation légèrement boostée\nNe pas surexposer — les ocres partent dans le blanc",
      diegosTip: "Venez juste après une pluie légère — les couleurs de la terre sont multipliées par 3 et le rouge brique devient vermillon.",
    },
    {
      keywords: ["le morne", "morne", "péninsule", "morne brabant"],
      locationName: "Le Morne Brabant — Coucher de soleil & vent kitesurf",
      bestTime: "Coucher de soleil (17h30–18h30)",
      phoneSettings: "Téléobjectif 85–135mm · f/5.6 · ISO 200 · Silouhette mode\nExposez sur le ciel, pas sur le rocher",
      diegosTip: "Placez-vous côté kitesurf au coucher — les cerfs-volants colorés dans la lumière orange font des images de carte postale absolument folles.",
    },
    {
      keywords: ["black river", "gorges", "cascade", "rivière noire", "forêt"],
      locationName: "Gorges de la Rivière Noire — Forêt primaire",
      bestTime: "Matin (7h–10h) lumière diffuse sous les arbres",
      phoneSettings: "Cascades · f/22 · ISO 100 · 2–4s · trépied · filtre ND\nForêt · ISO 800 · f/2.8 · 1/60s · bracketing expo",
      diegosTip: "Les cascades en pose longue créent un effet soyeux fascinant — mais un trépied est indispensable sur les sentiers boueux.",
    },
    {
      keywords: ["lagon", "plage", "corail", "belle mare", "trou aux biches", "snorkeling", "plongée", "sous-marin"],
      locationName: "Lagon mauricien — Sous-marin & eaux turquoise",
      bestTime: "10h–14h (soleil zénithal pour les couleurs sous-marines)",
      phoneSettings: "Sous l'eau · Mode aquatique · WB : Sunny · ISO 400 · f/4\nÀ la surface · Polarisant obligatoire pour voir le fond",
      diegosTip: "Pour la photo sous-marine depuis le bord, mettez simplement l'objectif sous la surface — les moitiés air/eau donnent des résultats époustouflants.",
    },
    {
      keywords: ["grand bassin", "ganga talao", "temple hindou", "caverne"],
      locationName: "Grand Bassin — Lac sacré hindou",
      bestTime: "Tôt le matin (6h30–8h) · Mahashivaratri (fête)",
      phoneSettings: "Temples · f/5.6 · ISO 200 · 1/160s · Polarisant\nPèlerins · f/2.8 · ISO 800 · 1/250s discret",
      diegosTip: "Restez discret et souriant — la communauté hindoue est accueillante mais les rites sont sacrés. Une tenue correcte est indispensable.",
    },
    {
      keywords: ["port louis", "capitale", "marché central", "caudan", "waterfront"],
      locationName: "Port Louis — Street photo & marché central",
      bestTime: "Heure bleue (6h–7h) · Heure du marché (9h–12h)",
      phoneSettings: "Street · f/2.8 · ISO 800 · 1/320s · discret, à hauteur d'œil\nArchitecture coloniale · f/8 · ISO 100 · grand angle",
      diegosTip: "Le marché central de Port Louis à 9h du matin est une explosion de couleurs et d'odeurs — c'est là que j'ai pris mes meilleures photos mauriciennes.",
    },
  ],

  // ── AFRIQUE DU SUD ────────────────────────────────────────────────────────
  SOUTH_AFRICA: [
    {
      keywords: ["cap", "cape town", "table mountain", "montagne de la table", "camps bay"],
      locationName: "Table Mountain & Cape Town — Vue panoramique",
      bestTime: "Lever du soleil ou coucher · Éviter le dessus de la montagne à midi",
      phoneSettings: "Panorama · f/8 · ISO 100 · Mode Pano horizontal\nNuit depuis Signal Hill · ISO 3200 · f/2.8 · 25s",
      diegosTip: "Montez en téléphérique à l'ouverture — le brouillard dit 'nappe de table' arrive vite et recouvre tout. La fenêtre est courte mais magique.",
    },
    {
      keywords: ["kruger", "safari", "big five", "réserve", "brousse", "savane"],
      locationName: "Kruger National Park — Safari wildlife",
      bestTime: "Lever du soleil (5h30–8h) · Fin d'après-midi (16h–18h30)",
      phoneSettings: "Téléobjectif 300–600mm · ISO 800 · f/5.6 · 1/500s min\nExposez sur l'œil de l'animal, pas sur le fond",
      diegosTip: "Un lion au coucher de soleil à contre-jour donne une photo légendaire avec 1/1000s — sous-exposez d'un stop pour garder le ciel orange.",
    },
    {
      keywords: ["garden route", "tsitsikamma", "knysna", "côte des jardins", "forêt"],
      locationName: "Garden Route — Forêts & falaises de l'Océan Indien",
      bestTime: "Matin brumeux · Heure dorée soir",
      phoneSettings: "Forêt · ISO 400 · f/4 · 1/80s · recherchez les rais de lumière\nFalaises · f/11 · ISO 100 · ND1000 pour mer longue exposition",
      diegosTip: "Les ponts suspendus de Tsitsikamma au lever du soleil dans la brume — c'est l'une des photos que je conseille à tous ceux qui font la Garden Route.",
    },
  ],

  // ── BOTSWANA ──────────────────────────────────────────────────────────────
  BOTSWANA: [
    {
      keywords: ["okavango", "delta", "pirogue", "mokoro", "marécage", "papyrus"],
      locationName: "Delta de l'Okavango — Coucher de soleil en mokoro",
      bestTime: "Coucher de soleil (17h–18h30)",
      phoneSettings: "Téléobjectif 200mm · ISO 400 · f/5.6 · 1/500s (oiseaux)\nCoucher de soleil · ISO 100 · exposez sur le ciel",
      diegosTip: "La pirogue au ras de l'eau vous met au niveau des grenouilles et des oiseaux — un angle unique qu'on ne voit nulle part ailleurs en Afrique.",
    },
    {
      keywords: ["chobe", "elephant", "éléphant", "rivière", "safari"],
      locationName: "Chobe River — Troupeaux d'éléphants",
      bestTime: "Heure dorée matin (6h–8h) · Heure dorée soir (16h30–18h)",
      phoneSettings: "Téléobjectif 300mm min · ISO 640 · f/5.6 · 1/640s\nÉvitez midi — lumière plate et animaux à l'ombre",
      diegosTip: "Prenez le bateau sur la rivière au lever du soleil — les éléphants traversent souvent à cette heure et le reflet dans l'eau est spectaculaire.",
    },
  ],

  // ── MADAGASCAR ────────────────────────────────────────────────────────────
  MADAGASCAR: [
    {
      keywords: ["baobab", "avenue", "morondava", "allée des baobabs"],
      locationName: "Allée des Baobabs — Morondava",
      bestTime: "Lever du soleil · Coucher de soleil · Brume matinale",
      phoneSettings: "Heure dorée · f/8 · ISO 100 · 1/200s · Silhouettes\nBrume · f/5.6 · ISO 400 · -1 stop d'exposition",
      diegosTip: "La brume du matin transforme complètement les baobabs — j'ai attendu 3 jours que les conditions soient parfaites. Ça valait chaque seconde.",
    },
    {
      keywords: ["tsingy", "bemaraha", "karst", "massif"],
      locationName: "Tsingy de Bemaraha — Forêt de pierre",
      bestTime: "8h–11h (lumière rasante dans les canyons)",
      phoneSettings: "Grand angle · f/11 · ISO 200 · 1/200s\nIntervalomètre et trépied si accès aux passerelles",
      diegosTip: "Les Tsingy au matin créent des jeux d'ombres et de lumière entre les aiguilles karstiques — mais protégez bien votre matériel des arêtes coupantes.",
    },
  ],

  // ── RÉUNION ───────────────────────────────────────────────────────────────
  REUNION: [
    {
      keywords: ["piton de la fournaise", "volcan", "lave", "cratère", "éruption"],
      locationName: "Piton de la Fournaise — Volcan actif",
      bestTime: "Nuit (éruption active) · Lever du soleil depuis le Pas de Bellecombe",
      phoneSettings: "Lave · ISO 1600 · f/4 · 1/30s · Trépied\nLever du soleil · ISO 100 · f/8 · Bracketing HDR",
      diegosTip: "Si une éruption est en cours, un trépied est votre meilleur ami — les photos longue exposition de lave rouge dans la nuit noire sont irréelles.",
    },
    {
      keywords: ["cirque", "mafate", "cilaos", "salazie", "randonnée", "rempart"],
      locationName: "Cirques de La Réunion — Paysages lunaires",
      bestTime: "Matin avant 10h · Lumière rasante sur les remparts",
      phoneSettings: "Drone (si réglementation autorise) ou grand angle\nf/8 · ISO 100 · HDR pour gérer contraste ombre/soleil",
      diegosTip: "Le Cirque de Mafate au lever du soleil, quand les nuages comblent les vallées comme un océan blanc — aucune photo ne rend pleinement justice à ce spectacle.",
    },
  ],

  // ── JAPON ─────────────────────────────────────────────────────────────────
  JAPAN: [
    {
      keywords: ["fuji", "kawaguchiko", "lac", "hakone", "mont fuji"],
      locationName: "Mont Fuji depuis le lac Kawaguchiko",
      bestTime: "Lever du soleil · Reflet parfait par vent nul",
      phoneSettings: "Téléobjectif 100–200mm · f/8 · ISO 100 · 1/250s\nReflet sur le lac · Polarisant pour effacer les reflets parasites",
      diegosTip: "Arrivez la veille au soir, dormez sur place et levez-vous à 4h30 — c'est uniquement ainsi que vous aurez le Fuji sans nuages et sans foule.",
    },
    {
      keywords: ["fushimi", "inari", "torii", "renard", "sanctuaire", "kyoto"],
      locationName: "Fushimi Inari — Tunnel de torii vermillons",
      bestTime: "5h–7h du matin (avant la foule massive)",
      phoneSettings: "Grand angle 16–24mm · f/5.6 · ISO 400 · 1/60s\nPoignée centrée dans le tunnel pour symétrie parfaite",
      diegosTip: "Passez la nuit à Kyoto et soyez à Fushimi Inari à l'ouverture à 5h — les torii dans la brume du matin sans personne, c'est une image qui change votre vie.",
    },
    {
      keywords: ["arashiyama", "bambou", "bambouseraie", "forêt de bambou"],
      locationName: "Forêt de bambous d'Arashiyama",
      bestTime: "6h–7h30 (brume légère et lumière filtrée)",
      phoneSettings: "Objectif standard 35–50mm · f/2.8 · ISO 400 · 1/125s\nVisez vers le ciel — les tiges convergentes donnent un effet cathédrale",
      diegosTip: "L'ombre et la lumière dans cette forêt changent toutes les cinq minutes — prenez le temps de vous asseoir et d'attendre le rayon parfait.",
    },
    {
      keywords: ["tokyo", "shibuya", "shinjuku", "carrefour", "nuit", "néons"],
      locationName: "Tokyo — Nuit néons & Carrefour de Shibuya",
      bestTime: "Nuit (19h–23h) · Pluie légère pour les reflets",
      phoneSettings: "Grand angle · Mode Nuit · ISO 1600 · f/2.8 · 1/15s\nCarrefour Shibuya : trépied depuis le Mag's Park au 2e étage",
      diegosTip: "Shibuya sous la pluie crée des reflets néons sur l'asphalte mouillé — des images de cyberpunk absolu. Emportez toujours un parapluie transparent.",
    },
  ],

  // ── THAÏLANDE ─────────────────────────────────────────────────────────────
  THAILAND: [
    {
      keywords: ["ayutthaya", "sukhothai", "temple", "bouddha", "wat", "prang"],
      locationName: "Temples d'Ayutthaya — Ruines bouddhistes",
      bestTime: "Lever du soleil (6h–8h) · Coucher de soleil · Nuit avec éclairage",
      phoneSettings: "Architecture · f/8 · ISO 100 · 1/160s · Grand angle\nNuit éclairée · ISO 800 · f/4 · 1/30s",
      diegosTip: "Un bouddha souriant en contre-jour au lever du soleil avec un ciel corail — voilà l'image typique de Thaïlande qui fait tout le travail à votre place.",
    },
    {
      keywords: ["phi phi", "phuket", "krabi", "railay", "plage", "falaises calcaires", "long tail"],
      locationName: "Plages de Krabi & Îles Phi Phi",
      bestTime: "Heure bleue (6h–7h) · Heure dorée soir (17h–18h30)",
      phoneSettings: "Plage · Polarisant · f/8 · ISO 100 · 1/250s\nBoat shot · 1/500s pour geler les éclaboussures",
      diegosTip: "Embarquez sur un bateau long-tail à 6h du matin pour Railay Beach — avant les tours organisés, la plage est déserte et la lumière est parfaite.",
    },
    {
      keywords: ["chiang mai", "doi inthanon", "doi suthep", "montagne", "brume", "nord"],
      locationName: "Doi Inthanon — Toit de la Thaïlande",
      bestTime: "Lever du soleil (5h30–7h) · brume dans les vallées",
      phoneSettings: "Brume · f/5.6 · ISO 400 · -1 EV · Longues focales\nCascades · f/22 · ISO 100 · 2–4s · Trépied",
      diegosTip: "La brume du matin dans les vallées de Chiang Mai vue depuis Doi Inthanon ressemble à une mer de nuages — planifiez pour être en haut à 6h.",
    },
  ],

  // ── VIETNAM ───────────────────────────────────────────────────────────────
  VIETNAM: [
    {
      keywords: ["ha long", "baie", "karst", "jonque", "rocher", "île calcaire"],
      locationName: "Baie de Ha Long — Karsts émergents",
      bestTime: "Lever du soleil (5h30–7h30) · Brume matinale",
      phoneSettings: "Brume · f/5.6 · ISO 400 · sous-exposez légèrement\nHeure bleue depuis le bateau · ISO 800 · f/4 · 1/30s",
      diegosTip: "Dormez sur une jonque pour photographier l'aube — quand la brume efface les jonques au loin et que seuls les karsts émergent, c'est magique.",
    },
    {
      keywords: ["hoi an", "lanterne", "vieille ville", "rivière", "pont japonais"],
      locationName: "Hoi An — Lanternes & vieille ville la nuit",
      bestTime: "Pleine lune (fête des lanternes) · Heure bleue (19h–20h30)",
      phoneSettings: "Lanternes · f/2.8 · ISO 800 · 1/30s · WB Tungsten\nRivière de nuit · f/4 · ISO 1600 · 1/15s",
      diegosTip: "La nuit de la pleine lune à Hoi An quand les milliers de lanternes flottent sur la rivière — planifiez votre séjour sur ce soir précis.",
    },
    {
      keywords: ["sapa", "rizières", "terrasses", "mu cang chai", "montagne", "ethnie"],
      locationName: "Rizières en terrasses de Sapa & Mu Cang Chai",
      bestTime: "Saison des moissons (sept–oct) · Lever du soleil",
      phoneSettings: "Drone ou point haut · f/8 · ISO 100 · Vue plongeante\nBrume · f/5.6 · longue focale pour comprimer les plans",
      diegosTip: "Engagez un guide de l'ethnie locale — ils vous emmènent sur des points de vue que les tours standard n'atteignent jamais.",
    },
  ],

  // ── MAROC ─────────────────────────────────────────────────────────────────
  MOROCCO: [
    {
      keywords: ["sahara", "erg", "dune", "merzouga", "chegaga", "désert"],
      locationName: "Erg Chebbi — Dunes du Sahara",
      bestTime: "Lever du soleil · Coucher de soleil · Nuit (Voie Lactée)",
      phoneSettings: "Dunes dorées · f/8 · ISO 100 · 1/250s · Grand angle\nVoie Lactée · f/2.8 · ISO 3200 · 20s · Trépied",
      diegosTip: "Partez en chameau 1h avant le lever du soleil — les traces dans le sable s'effacent tôt et la lumière rasante sur les crêtes des dunes est inimitable.",
    },
    {
      keywords: ["médina", "souk", "fès", "fes", "marrakech", "riad", "moucharabieh", "tannerie"],
      locationName: "Médinas de Fès ou Marrakech",
      bestTime: "Matin (8h–11h) · Lumière dans les souks couverts",
      phoneSettings: "Souk · f/2.8 · ISO 800 · 1/125s · Autofocus suivi\nTannerie de Fès · f/5.6 · ISO 200 · Grand angle",
      diegosTip: "La tannerie Chouara de Fès depuis les terrasses des maroquineries — montez à 9h avec un guide local, c'est une des photos les plus emblématiques du Maroc.",
    },
    {
      keywords: ["ait ben haddou", "kasbah", "kasba", "atlas", "gorges du dadès", "todra"],
      locationName: "Aït Ben Haddou & Gorges du Dadès",
      bestTime: "Lever du soleil (orangé sur la pisé) · Heure dorée soir",
      phoneSettings: "Kasba · f/8 · ISO 100 · 1/200s · Lumière frontale évitée\nGorges Todra · Grand angle · f/11 · 1/160s · Polarisant",
      diegosTip: "La kasba d'Aït Ben Haddou avec le soleil levant sur les pisés ocres — traversez la rivière tôt le matin pour avoir la scène pour vous seul.",
    },
  ],

  // ── INDONÉSIE ─────────────────────────────────────────────────────────────
  INDONESIA: [
    {
      keywords: ["batur", "agung", "volcan", "caldeira", "sunrise", "bali"],
      locationName: "Mont Batur — Lever du soleil sur le volcan",
      bestTime: "Sommet à 5h30 (départ de nuit à 3h30)",
      phoneSettings: "Ciel coloré · f/5.6 · ISO 400 · bracketing\nMer de nuages · f/8 · ISO 100 · Grand angle",
      diegosTip: "Le trek de nuit avec lampe frontale jusqu'au sommet du Batur est intense mais le lever du soleil au-dessus des nuages récompense chaque effort.",
    },
    {
      keywords: ["rice", "rizière", "tegallalang", "jatiluwih", "terrasse", "riz"],
      locationName: "Rizières en terrasses de Tegallalang",
      bestTime: "Matin (7h–10h) avant la chaleur · Lumière rasante",
      phoneSettings: "f/8 · ISO 100 · Grand angle · 1/160s\nSilhouette agriculteur · contre-jour · f/5.6 · ISO 200",
      diegosTip: "Descendez dans les terrasses plutôt que de rester au bord de la route — les photos depuis le bas, avec les palmiers qui émergent, sont bien plus intéressantes.",
    },
    {
      keywords: ["komodo", "raja ampat", "plongée", "requin", "manta", "manta ray"],
      locationName: "Raja Ampat — Paradis sous-marin",
      bestTime: "10h–14h (soleil zénithal, visibilité maximale)",
      phoneSettings: "Sous-eau · Mode aquatique · WB Auto · f/4 · ISO 400\nMantas · Téléobjectif waterproof · 1/250s",
      diegosTip: "Le musée vivant de Raja Ampat est si riche que le plus difficile est de choisir quoi photographier — je vous conseille de laisser tomber le cliché du poisson et de chercher les détails (nudibranche, crabe fantôme).",
    },
  ],

  // ── INDE ─────────────────────────────────────────────────────────────────
  INDIA: [
    {
      keywords: ["taj mahal", "agra", "mausolée"],
      locationName: "Taj Mahal — Aube sur le marbre blanc",
      bestTime: "Ouverture (5h30) · Brume matinale d'octobre à février",
      phoneSettings: "f/8 · ISO 100 · 1/200s · Grand angle\nReflet bassin · Polarisant · Mise au point sur le dôme",
      diegosTip: "Le Taj dans la brume matinale d'hiver, avec les premières lumières roses sur le marbre blanc — soyez là à l'ouverture, jamais à 10h avec tout le monde.",
    },
    {
      keywords: ["varanasi", "gange", "ghats", "crémation", "fleuve", "bateau"],
      locationName: "Varanasi — Ghats du Gange à l'aube",
      bestTime: "Lever du soleil depuis un bateau (5h–7h)",
      phoneSettings: "f/2.8 · ISO 800 · 1/250s (discret, silhouettes)\nBrume sur le Gange · ISO 400 · -1 EV · Longue focale",
      diegosTip: "Louez un petit bateau seul avec un rameur, sans guide touristique — la cérémonie aarti du soir ou l'aube avec la brume sur le Gange sont les deux moments les plus photogéniques de toute l'Inde.",
    },
    {
      keywords: ["jaisalmer", "rajasthan", "fort", "désert du thar", "pushkar", "jaipur"],
      locationName: "Rajasthan — Forts & désert du Thar",
      bestTime: "Heure dorée matin et soir · Brume de chaleur à éviter",
      phoneSettings: "Architecture jaune · f/8 · ISO 100 · 1/200s\nPortraits · f/2.8 · ISO 400 · 1/320s · Lumière latérale",
      diegosTip: "Les turbans colorés des Rajpoutes dans la lumière dorée du soir à Jaisalmer donnent des portraits qui rivalisent avec les meilleurs studios — approchez-vous et souriez.",
    },
  ],

  // ── PAR DÉFAUT ────────────────────────────────────────────────────────────
  DEFAULT: [
    {
      keywords: ["coucher de soleil", "sunset", "golden hour", "heure dorée", "ciel", "horizon"],
      locationName: "Golden Hour — Heure dorée universelle",
      bestTime: "30 min avant et 30 min après le coucher du soleil",
      phoneSettings: "f/8 · ISO 100 · Exposez sur le ciel · Polarisant\nSilhouettes : -1 EV · Mise au point sur l'horizon",
      diegosTip: "La règle que j'applique partout : soyez sur place 30 min avant le coucher du soleil, restez 30 min après — les 5 meilleures minutes viennent souvent quand tout le monde est déjà reparti.",
    },
    {
      keywords: ["nuit", "étoile", "voie lactée", "lune", "astronomie", "ciel étoilé"],
      locationName: "Astronomie — Ciel étoilé & Voie Lactée",
      bestTime: "Nuit noire (nouvelle lune) · 21h–3h",
      phoneSettings: "Mode Pro · f/2.8 max · ISO 3200–6400 · 15–25s · Trépied\nRègle des 500 : 500 ÷ focale (mm) = pose max sans filé",
      diegosTip: "Éloignez-vous de tout éclairage urbain (minimum 30 km), attendez 20 min que vos yeux s'adaptent à l'obscurité — vous serez stupéfait de ce que voit votre téléphone en mode Pro.",
    },
    {
      keywords: ["lever du soleil", "sunrise", "matin", "aube", "brume"],
      locationName: "Heure bleue & Lever du soleil",
      bestTime: "30 min avant le lever du soleil (heure bleue) jusqu'à 1h après",
      phoneSettings: "f/5.6 · ISO 400 · WB : Nuageux pour réchauffer\nBrume : -1 EV · Longue focale pour comprimer les plans",
      diegosTip: "L'heure bleue juste avant le lever du soleil est souvent plus belle que le lever lui-même — les tons indigo et violet sur le paysage sont incomparables.",
    },
    {
      keywords: ["paysage", "montagne", "forêt", "cascade", "lac", "rivière", "nature"],
      locationName: "Paysage naturel — Longue exposition",
      bestTime: "Matin (7h–10h) · Soir (16h–coucher) · Météo nuageuse = bonus",
      phoneSettings: "Cascade · f/22 · ISO 100 · 2–8s · Trépied + ND1000\nPanorama · f/8 · ISO 100 · Mode Pano · Overlap 30%",
      diegosTip: "Un ciel nuageux n'est pas votre ennemi — les nuages en mouvement adoucissent la lumière et donnent une profondeur dramatique que le beau temps ne peut pas offrir.",
    },
  ],
};

// ── Matching function ──────────────────────────────────────────────────────
/**
 * Returns 0-2 photo spots relevant to the current day.
 * Matches keywords case-insensitively against day title + description.
 */
export function getPhotoSpotsForDay(dayTitle = "", dayDescription = "", destination = "") {
  const country = detectCountry(destination);
  const spots = PHOTO_SPOTS[country] || PHOTO_SPOTS.DEFAULT;
  const text = `${dayTitle} ${dayDescription}`.toLowerCase();

  const matched = spots.filter(spot =>
    spot.keywords.some(kw => text.includes(kw.toLowerCase()))
  );

  // Fall back to first DEFAULT spot if no match (for generic photography tips)
  if (matched.length === 0) return [];

  return matched.slice(0, 2);
}
